/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React from 'react';

// eslint-disable-next-line @typescript-eslint/consistent-type-imports, @atlaskit/ui-styling-standard/use-compiled -- emotion jsx pragma; go/DSP-18766
import { css, jsx } from '@emotion/react';
// oxlint-ignore @typescript-eslint/consistent-type-imports -- classic @jsx jsx factory + jsx.JSX.Element types
import memoizeOne from 'memoize-one';

import type { Layout as ExtensionLayout } from '@atlaskit/adf-schema/extensions';
import { getNodeRenderer } from '@atlaskit/editor-common/extensions';
import type {
	ExtensionHandlers,
	ExtensionParams,
	ExtensionProvider,
	MultiBodiedExtensionActions,
	Parameters,
} from '@atlaskit/editor-common/extensions';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { WithProviders } from '@atlaskit/editor-common/provider-factory';
import { getExtensionRenderer } from '@atlaskit/editor-common/utils';
import type { Mark as PMMark } from '@atlaskit/editor-prosemirror/model';
import { token } from '@atlaskit/tokens';

import type { RendererContext } from '../react/types';

interface Props {
	actions?: MultiBodiedExtensionActions;
	children: ({
		isExtensionProviderPending,
		node,
		result,
	}: {
		isExtensionProviderPending: boolean;
		node: ExtensionParams<Parameters>;
		result?: JSX.Element | null;
	}) => JSX.Element;
	extensionHandlers?: ExtensionHandlers;
	extensionKey: string;
	extensionType: string;
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	getContent?: () => any;
	layout?: ExtensionLayout;
	localId?: string;
	marks?: PMMark[];
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	parameters?: any;
	providers?: ProviderFactory;
	rendererContext: RendererContext;
	text?: string;
	type: 'extension' | 'inlineExtension' | 'bodiedExtension' | 'multiBodiedExtension';
}

interface State {
	extensionProvider?: ExtensionProvider | null;
}

const inlineExtensionStyle = css({
	display: 'inline-block',
	maxWidth: '100%',
	verticalAlign: 'middle',
	// 0px margin top is important here.
	// When running on server-side emotion will generate style tags before elements.
	// This caused packages/editor/editor-common/src/styles/shared/block-marks.ts to override the margin-top.
	// However as soon as the styles are extracted to <head> it adds back the margin.
	// The timing is tricky as it happens to be when UFO collects the dimension for the placeholder for TTVC calculation.
	// This resulted 1px mismatch on the image. Further cause everything on page to shift by 1px.
	// es-lint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
	margin: `0px 1px ${token('space.050')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& .rich-media-item': {
		maxWidth: '100%',
	},
});

const plainTextMacroStyle = css({
	display: 'inline',
	verticalAlign: 'baseline',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-macro-body]': {
		display: 'inline',
	},
});

/** Renders extension (macro) nodes inside the ADF renderer. */
export default function ExtensionRenderer(props: Props): jsx.JSX.Element {
	const {
		extensionHandlers,
		rendererContext,
		extensionType,
		extensionKey,
		parameters,
		getContent,
		text,
		type,
		localId,
		marks,
		actions,
		children,
	} = props;

	const isMounted = React.useRef(true);
	const localGetNodeRenderer = React.useMemo(() => memoizeOne(getNodeRenderer), []);
	/**
	 * null -> provider promise not yet settled (pending)
	 * undefined -> provider promise rejected (settled, no provider available)
	 * <provider> -> provider promise resolved
	 */
	const [extensionProvider, setExtensionProvider] = React.useState<
		ExtensionProvider | null | undefined
	>(null);

	const handleProvider = React.useCallback(
		(_name: keyof State, providerPromise?: Promise<ExtensionProvider>) => {
			providerPromise?.then(
				(provider) => {
					if (isMounted.current) {
						setExtensionProvider(provider);
					}
				},
				() => {
					// Consumers can use this to distinguish a rejected provider from a pending one
					if (isMounted.current) {
						setExtensionProvider(undefined);
					}
				},
			);
		},
		[],
	);

	const renderExtensionNode = React.useCallback(
		(
			extensionProvider: ExtensionProvider | null | undefined,
			isExtensionProviderPending: boolean,
		) => {
			const fragmentLocalId = marks?.find((m) => m.type.name === 'fragment')?.attrs?.localId;

			// Extension handlers read the macro body off `node.content`, so this is the one place that
			// always has to serialize. `getContent` is what makes it a single pass instead of one per
			// ancestor node.
			const node = {
				type,
				extensionKey,
				extensionType,
				parameters,
				content: getContent?.() || text,
				localId,
				fragmentLocalId,
			};

			const isPlainTextMacro = Boolean(node?.parameters?.macroParams?.__bodyContent);

			let result: JSX.Element | null = null;

			try {
				if (extensionHandlers && extensionHandlers[extensionType]) {
					const render = getExtensionRenderer(extensionHandlers[extensionType]);
					result = render(node, rendererContext.adDoc);
				}

				if (!result && extensionProvider) {
					const NodeRenderer = localGetNodeRenderer(extensionProvider, extensionType, extensionKey);
					if (node.type === 'multiBodiedExtension') {
						result = <NodeRenderer node={node} actions={actions} />;
					} else if (node.type === 'inlineExtension') {
						result = (
							<InlineNodeRendererWrapper isPlainTextMacro={isPlainTextMacro}>
								<NodeRenderer node={node} />
							</InlineNodeRendererWrapper>
						);
					} else {
						result = <NodeRenderer node={node} />;
					}
				}
			} catch {
				/** We don't want this error to block renderer */
				/** We keep rendering the default content */
			}

			return children({ isExtensionProviderPending, node, result });
		},
		[
			actions,
			children,
			getContent,
			extensionHandlers,
			extensionKey,
			extensionType,
			localGetNodeRenderer,
			localId,
			marks,
			parameters,
			rendererContext?.adDoc,
			text,
			type,
		],
	);

	const setupAndRenderExtensionNode = React.useCallback(
		(providers: { extensionProvider?: Promise<ExtensionProvider> }) => {
			if (extensionProvider === null && providers.extensionProvider) {
				handleProvider('extensionProvider', providers.extensionProvider);
			}

			return renderExtensionNode(
				extensionProvider,
				Boolean(providers.extensionProvider) && extensionProvider === null,
			);
		},
		[extensionProvider, handleProvider, renderExtensionNode],
	);

	React.useEffect(() => {
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, []);

	if (!props.providers) {
		return setupAndRenderExtensionNode({});
	}

	return (
		<WithProviders
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			providers={['extensionProvider']}
			providerFactory={props.providers}
			renderNode={setupAndRenderExtensionNode}
		/>
	);
}

export const InlineNodeRendererWrapper = ({
	children,
	isPlainTextMacro,
	ssrPlaceholder,
	ssrPlaceholderReplace,
}: React.PropsWithChildren<{
	isPlainTextMacro?: boolean;
	ssrPlaceholder?: string;
	ssrPlaceholderReplace?: string;
}>): jsx.JSX.Element => {
	return (
		<div
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className={`inline-extension-renderer ${isPlainTextMacro ? 'plain-text-macro' : ''}`}
			css={[inlineExtensionStyle, isPlainTextMacro && plainTextMacroStyle]}
			data-ssr-placeholder={ssrPlaceholder}
			data-ssr-placeholder-replace={ssrPlaceholderReplace}
		>
			{children}
		</div>
	);
};
